import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";

export type CategoryFilterValue =
  | "summary"
  | "apis"
  | "models"
  | "flows"
  | "rags"
  | "deployments";

const OPTION_IDS: CategoryFilterValue[] = [
  "summary",
  "apis",
  "models",
  "flows",
  "rags",
  "deployments",
];

type Props = {
  value: CategoryFilterValue;
  onChange: (value: CategoryFilterValue) => void;
  showDeployments?: boolean;
  className?: string;
};

export function CategoryFilter({
  value,
  onChange,
  showDeployments = false,
  className,
}: Props) {
  const { t } = useTranslation();
  const options = showDeployments
    ? OPTION_IDS
    : OPTION_IDS.filter((id) => id !== "deployments");

  return (
    <div
      role="tablist"
      aria-label={t("usageAndCost.categories.ariaLabel")}
      className={cn(
        "inline-flex items-center gap-1 rounded-xl border border-border bg-secondary p-1",
        className,
      )}
    >
      {options.map((id) => {
        const active = id === value;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            className={cn(
              "relative rounded-xl px-4 py-1.5 text-sm font-medium transition-colors outline-none",
              active
                ? "text-foreground"
                : "text-foreground/70 hover:text-foreground",
            )}
          >
            {active && (
              <motion.span
                layoutId="category-filter-active"
                className="absolute inset-0 rounded-xl bg-background"
                transition={{ type: "spring", stiffness: 420, damping: 36 }}
              />
            )}
            <span className="relative z-10">
              {t(`usageAndCost.categories.${id}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
